import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_COLORS, labelRol, colorRol } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

import { useConfirm } from "@/context/ConfirmContext";
export default function Usuarios() {
  const { user } = useAuth();
  const confirmar = useConfirm();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nombre: "", email: "", password: "", role: "comercial" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/roles-map").then((r) => setRoles(r.data)).catch(() => {});
  }, []);

  if (user && !user.es_admin) return <Navigate to="/" replace />;

  const openNew = () => { setEditId(null); setForm({ nombre: "", email: "", password: "", role: "comercial" }); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setForm({ nombre: u.nombre, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { nombre: form.nombre, role: form.role, ...(form.password ? { password: form.password } : {}) });
      } else {
        await api.post("/users", form);
      }
      toast.success("Usuario guardado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!(await confirmar({ titulo: `¿Eliminar a ${u.nombre}?`, accion: "Eliminar usuario", peligro: true }))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Usuarios</h1>
          <p className="text-slate-500 mt-1">Gestiona las cuentas y sus roles de acceso.</p>
        </div>
        <Button className="rounded-lg" onClick={openNew} data-testid="new-user-button"><Plus size={16} className="mr-1" /> Nuevo usuario</Button>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-3 font-semibold">Nombre</th>
              <th className="px-5 py-3 font-semibold">Correo</th>
              <th className="px-5 py-3 font-semibold">Rol</th>
              <th className="px-5 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium text-slate-800">{u.nombre}</td>
                <td className="px-5 py-3 text-slate-500">{u.email}</td>
                <td className="px-5 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${colorRol(u.role)}`}>{labelRol(u.role)}</span></td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openEdit(u)} className="text-slate-400 hover:text-blue-600 p-1.5 transition-colors" data-testid={`edit-user-${u.id}`}><Pencil size={15} /></button>
                  {u.id !== user.id && <button onClick={() => del(u)} className="text-slate-400 hover:text-red-600 p-1.5 transition-colors" data-testid={`delete-user-${u.id}`}><Trash2 size={15} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="user-nombre-input" /></div>
            <div className="space-y-1.5"><Label>Correo</Label><Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>{editId ? "Nueva contraseña (opcional)" : "Contraseña"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy || !form.nombre || (!editId && (!form.email || !form.password))} data-testid="user-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
